import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const STATUS = {
  approved: { label: 'Aprovado', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  pending: { label: 'Pendente', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  suspended: { label: 'Suspenso', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
};

export function MerchantBusinessesPage() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/merchant/businesses');
      setBusinesses(data.data || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao carregar estabelecimentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  const statusOf = (b) => {
    if (!b.is_active) return STATUS.suspended;
    if (!b.is_approved) return STATUS.pending;
    return STATUS.approved;
  };

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={() => navigate('/perfil')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Meus estabelecimentos</h1>
        </div>
        <button
          onClick={() => navigate('/comerciante/solicitar')}
          style={{
            background: 'var(--nc-surface)', color: 'var(--nc-primary)', border: 'none', borderRadius: 10,
            padding: '11px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 6
          }}
        >
          + Cadastrar novo comércio
        </button>
      </div>

      <div style={{ padding: 16 }}>
        {loading ? <Loading message="Carregando estabelecimentos..." /> : error ? (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13, marginBottom: 12
          }}>
            {error}
            <button onClick={fetchBusinesses} className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }}>Tentar novamente</button>
          </div>
        ) : businesses.length === 0 ? (
          <EmptyState
            icon="🏪"
            title="Nenhum estabelecimento"
            description="Cadastre seu comércio para aparecer no mapa da sua cidade após aprovação."
            action={<button className="nc-btn-primary" onClick={() => navigate('/comerciante/solicitar')}>Cadastrar comércio</button>}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {businesses.map(b => {
              const st = statusOf(b);
              return (
                <div key={b.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10, background: 'var(--nc-surface-alt)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
                      overflow: 'hidden'
                    }}>
                      {b.logo_url ? (
                        <img src={b.logo_url} alt={`Logo de ${b.name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : '🏪'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>{b.name}</div>
                      <div className="nc-hint" style={{ marginTop: 2 }}>
                        {b.city_name}/{b.state} • {b.category}
                      </div>
                      <span style={{
                        display: 'inline-block', marginTop: 6, fontSize: 10, fontWeight: 600,
                        color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 10
                      }}>
                        {st.label}
                      </span>
                    </div>
                  </div>
                  {!b.is_approved && b.is_active && (
                    <div style={{ fontSize: 12, color: 'var(--nc-warning-text)', background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                      ⏳ Aguardando aprovação da equipe Nossa Cidade.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => navigate(`/comerciante/comercio/${b.id}`)}
                      style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', color: 'var(--nc-primary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => navigate(`/comerciante/publicacoes?business_id=${b.id}`)}
                      style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', color: 'var(--nc-primary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                    >
                      Publicações ({b.post_count ?? 0})
                    </button>
                    {/* LE-020 (S4-b): acesso ao impulsionamento do comércio */}
                    <button
                      onClick={() => navigate(`/comerciante/impulsionar/${b.id}`)}
                      style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', color: 'var(--nc-primary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                    >
                      🚀 Impulsionar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
